import * as React from 'react';
import * as _ from 'underscore';
import * as queryString from 'query-string';

import { IViz, VizType } from '../../../ts/models/Viz';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';

import { AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater } from './AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { IVizScope } from '../../../ts/directives/tbViz';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { VizPathInitialState } from '../../reducers/VizPathReducer';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { selectVizPath } from '../../selectors/VizPathSelector';
import { setVizPath } from '../../actions/spaces/SetVizPathAction';

export const SiteAdminAnalysisVizPathPrefix = 'tabbed_site_views/';
export const ServerAdminAnalysisVizPathPrefix = 'tabbed_admin_views/';
export const SiteAdminBackgroundJobsVizPathPrefix = 'background_jobs_site/';
export const ServerAdminBackgrounderDelaysVizPathPrefix = 'backgrounder_delays/';
export const ServerAdminCurrentlyRunningJobsVizPathPrefix = 'currently_running_jobs/';
export const SiteAdminBackgrounderDashboardVizPathPrefix = 'backgrounder_dashboard_site/';
export const ServerAdminBackgrounderDashboardVizPathPrefix = 'backgrounder_dashboard/';
export const ServerAdminStaleContentVizPathPrefix = 'stale_content/';
export const ServerAdminStaleContentExtensionVizPathPrefix = 'stale_content_extension/';
export const SiteAdminStaleContentVizPathPrefix = 'stale_content_site_admin/';
export const SiteAdminStaleContentExtensionVizPathPrefix = 'stale_content_extension_site_admin/';
export const ServerAdminServerOverviewVizPathPrefix = 'server_overview/';
export const SiteAdminServerOverviewVizPathPrefix = 'server_overview_site_admin/';
export const SiteAdminAskDataUsageDashboardVizPathPrefix = 'askdata_dashboard_site/';
export const ServerAdminAskDataDashboardVizPathPrefix = 'askdata_dashboard_server/';
export const SiteAdminDqwAuditDashboardVizPathPrefix = 'dqw_audit_dashboard_site/';
export const ServerAdminDqwAuditDashboardVizPathPrefix = 'dqw_audit_dashboard_server/';

export interface AdminAnalysisVizSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly sheetUrlId: AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.AdminViewName;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly isServerAnalysis: boolean;
  readonly renderSpace: (angularContext: AngularContext, setupScope: (scope: ng.IScope) => ng.IScope) => JSX.Element;
}

export interface AdminAnalysisVizSpaceProviderStateProps {
  readonly vizPath: string;
}

export interface AdminAnalysisVizSpaceProviderDispatchProps {
  readonly setVizPath: (vizPath: string) => void;
}

const template = `<div ng-include="'adminVizViewer.html'"></div>`;

export const renderAdminAnalysisVizSpace = (angularContext: AngularContext, setupScope: (scope: ng.IScope) => ng.IScope): JSX.Element => {
  // the parent div here is important to ensure AngularDirective plays nice when unmounting
  return (
    <div>
      <AngularDirective
        template={template}
        angularContext={angularContext}
        setupScope={setupScope}
      />
    </div>
  );
};

export function getAdminVizPathPrefix(sheetUrlId: AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.AdminViewName, isServerAnalysis: boolean): string {
  switch (sheetUrlId) {
    case 'BackgrounderDashboard':
      return isServerAnalysis
        ? ServerAdminBackgrounderDashboardVizPathPrefix
        : SiteAdminBackgrounderDashboardVizPathPrefix;
    case 'StaleContent':
      return isServerAnalysis
        ? (FeatureFlags.staleContentExtensionEnabled ? ServerAdminStaleContentExtensionVizPathPrefix : ServerAdminStaleContentVizPathPrefix)
        : (FeatureFlags.staleContentExtensionEnabled ? SiteAdminStaleContentExtensionVizPathPrefix   : SiteAdminStaleContentVizPathPrefix);
    case 'ServerOverview':
      return ServerAdminServerOverviewVizPathPrefix;
    case 'ServerOverviewSiteAdmin':
      return SiteAdminServerOverviewVizPathPrefix;
    case 'AskDataUsage':
      return isServerAnalysis ? ServerAdminAskDataDashboardVizPathPrefix : SiteAdminAskDataUsageDashboardVizPathPrefix;
    case 'AskDataPerformance':
      return ServerAdminAskDataDashboardVizPathPrefix;
    case 'AuditDqwDashboard':
      return isServerAnalysis ? ServerAdminDqwAuditDashboardVizPathPrefix : SiteAdminDqwAuditDashboardVizPathPrefix;
    default:
      return isServerAnalysis ? ServerAdminAnalysisVizPathPrefix : SiteAdminAnalysisVizPathPrefix;
  }
}

export type AdminAnalysisVizSpaceProviderProps = AdminAnalysisVizSpaceProviderOwnProps & AdminAnalysisVizSpaceProviderStateProps & AdminAnalysisVizSpaceProviderDispatchProps;

export class AdminAnalysisVizSpaceProvider extends React.Component<AdminAnalysisVizSpaceProviderProps> {

  private spaceScope: IVizScope = null;

  public constructor(props: AdminAnalysisVizSpaceProviderProps) {
    super(props);
    this.state = {
      hasError: false
    };
  }

  public render() {
    return this.props.renderSpace(this.props.angularContext, this.setupScope);
  }

  public componentDidMount() {
    // initial viz path for analysis vizzes
    this.props.setVizPath(getAdminVizPathPrefix(this.props.sheetUrlId, this.props.isServerAnalysis) + this.props.sheetUrlId);
  }

  public componentDidUpdate(prevProps: AdminAnalysisVizSpaceProviderProps) {
    const vizPathFromSheetUrlId = getAdminVizPathPrefix(this.props.sheetUrlId, this.props.isServerAnalysis) + this.props.sheetUrlId;
    if (prevProps.sheetUrlId !== this.props.sheetUrlId && vizPathFromSheetUrlId !== this.props.vizPath) {
      // the url sheetId changed (because the sheetUrlId is different on the props)
      // but the vizPath is not updated yet. So update it and reload the viz.
      this.props.setVizPath(vizPathFromSheetUrlId);

      if (this.spaceScope) {
        const previousViz: IViz = this.spaceScope.viz;

        this.spaceScope.viz = {
          path: vizPathFromSheetUrlId,
          vizType: VizType.Admin,
          params:  queryString.parse(this.props.routeLocationSearch || ''),
          allowReloadViz: true,  // this needs to be true so we can reload the viz when the url changes

          reloadViz: previousViz.reloadViz,
          isExplicitOriginalViz: previousViz.isExplicitOriginalViz,
          instanceId: previousViz.instanceId,
        };
        this.spaceScope.$apply();
        this.spaceScope.viz.reloadViz && this.spaceScope.viz.reloadViz();
      }
    }
  }

  public shouldComponentUpdate(nextProps: AdminAnalysisVizSpaceProviderProps) {
    if(this.props.isServerAnalysis !== nextProps.isServerAnalysis) {
      return true;
    }

    if (nextProps.vizPath !== VizPathInitialState &&
        nextProps.vizPath !== this.props.vizPath &&
        nextProps.vizPath !== (getAdminVizPathPrefix(this.props.sheetUrlId, this.props.isServerAnalysis) + nextProps.sheetUrlId)) {
          // vizPath changed via user clicking on viz tab (that's why the url does not match the new vizPath-- see tbViz),
          // so update the url and don't rerender
          return false;
    }

    return true;
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as IVizScope;

    childScope.viz = {
      path: getAdminVizPathPrefix(this.props.sheetUrlId, this.props.isServerAnalysis) + this.props.sheetUrlId,
      vizType: VizType.Admin,
      params:  queryString.parse(this.props.routeLocationSearch || ''),
      allowReloadViz: true  // this needs to be true so we can reload the viz when the url changes
    };

    this.spaceScope = childScope;
    return childScope;
  }
}

const mapStateToProps: MapStateToProps<AdminAnalysisVizSpaceProviderStateProps> = (appState) => ({
  vizPath: selectVizPath(appState)
});

const mapDispatchToProps: MapDispatchToProps<AdminAnalysisVizSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setVizPath
}, dispatch);

export const ConnectedAdminAnalysisVizSpaceProvider: React.ComponentClass<AdminAnalysisVizSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(AdminAnalysisVizSpaceProvider);
